import React, { useEffect, useState } from "react";
import {
  FormActions,
  FormContainer,
  ModalContent,
  ModalOverlay,
} from "../../components/FormBuilder/CustomForm/FormFiller";
import styled from "styled-components";
import FormTitle from "../../components/form/FormTitle";
import { Button } from "../../components/form/Button";
import NewContactIcon from "../../assets/icons/NewContactIcon";
import CloseIcon from "../../assets/icons/CloseIcon";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import StyledText from "../../components/StyledComponents/StyledText";
import FormikInputField from "../../components/Formik/FormikInputField";
import { CorporateTheme } from "../../utils/themes";
import FormikSelect from "../../components/Formik/FormikSelect";
import FormikCheckBox from "../../components/Formik/FormikCheckBox";
import { contactRolesApi } from "../../api/contactRoles";
import { useToast } from "../../components/toast/ToastContext";
import EditRoleSkeleton from "./EditRoleSkeleton";
import { useTheme } from "../../middleware/ThemeContext";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { isRejectedWithValue } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import SuccessTick from "../../assets/icons/SuccessTick";
import WarningCross from "../../assets/icons/WarningCross";
import { Warning } from "@mui/icons-material";
import WarningYellowIcon from "../../assets/icons/WarningYellowIcon";
import { useNavigate } from "react-router-dom";
import AlertNotice from "../memberships/dashboard/Components/AlertNotice";

interface FormAddRole {
  onClose: () => void;
  id?: number | null;
  onSuccess: () => void;
}

const AddContactRoles: React.FC<FormAddRole> = ({ onClose, id, onSuccess }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [initialValues, setInitialValues] = useState({
    name: "",
    roleType: "",
    autoAssign: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const showToast = useToast();

  useEffect(() => {
    const fetchRoleData = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await contactRolesApi.getRoleById(id);
        setInitialValues({
          name: response.name || "",
          roleType: response.roleType || "",
          autoAssign: response.autoAssign || false,
        });
      } catch (error) {
        console.error("Failed to fetch role details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoleData();
  }, [id]);

  const setScopeSchema = Yup.object().shape({
    name: Yup.string().required("Required"),
    roleType: Yup.string().required("Required"),
  });

  const handleSubmit = async (values: {
    name: string;
    roleType: string;
    // autoAssign?: boolean;
  }) => {
    if (!id) {
      setIsSubmitting(true);
      try {
        await contactRolesApi.createRole(values);
        showToast("Success", "Contact role added successfully.", "success");
        setIsSubmitting(false);
        if (values.roleType === "Custom") {
          navigate("/home/roles-permissions");
        }
        onClose();
        onSuccess();
      } catch (error: unknown) {
        let errorMessage = (error as any).response.data.message;
        showToast("Error", errorMessage, "error");
        setIsSubmitting(false);
      }
    } else if (id) {
      try {
        setIsSubmitting(true);
        await contactRolesApi.updateRole(id, values);
        showToast("Success", "Contact role updated successfully", "success");
        setIsSubmitting(false);
        onSuccess();

        // if (values.roleType === 'Custom') {
        //     navigate('/home/roles-permissions');
        // } else {
        //     setIsSubmitting(true);
        //     await contactRolesApi.updateRole(id, values);
        //     showToast('Success', 'Contact role updated successfully', 'success');
        //     setIsSubmitting(false);
        //     onSuccess();
        // }
        onClose();
      } catch (error) {
        let errorMessage = (error as any).response.data.message;

        showToast("Error", errorMessage, "error");
        setIsSubmitting(false);
      }
    }
  };

  const role_type_detail = {
    Viewer: [
      // { icon: <SuccessTick />, text: 'View org/chapter profile' },
      // { icon: <SuccessTick />, text: 'View contact list' },
      // { icon: <SuccessTick />, text: 'View assigned forms' },
      // { icon: <SuccessTick />, text: 'View committee rosters' },
      // { icon: <WarningCross />, text: 'Submit or edit anything' },
      // { icon: <WarningCross />, text: 'Assign roles' },
      // { icon: <WarningCross />, text: 'Access billing' }
      "View org details",
      "View contacts",
      "Submit own forms",
      "View Memberships",
      "View Directory & pages",
    ],
    staffContact: [
      "View org details",
      "View contacts",
      "Edit contacts",
      "Submit own forms",
      "Submit on behalf of org",
      "View Memberships",
      "View Directory & pages",
    ],
    Manager: [
      "View org details",
      "Edit org details",
      "View contacts",
      "Add/Remove contacts",
      "Edit contacts",
      "Manage child orgs (Add/Edit)",
      "Assign/manage roles",
      "Submit own forms",
      "Submit on behalf of org",
      "Manage org submissions",
      "View billing/invoices",
      "View Memberships",
      "Manage/renew membership",
      "View Directory & pages",
    ],
    leadContact: [
      "View org details",
      "Edit org details",
      "View contacts",
      "Add/Remove contacts",
      "Edit contacts",
      "Manage child orgs (Full access)",
      "Assign/manage roles",
      "End Memberships",
      "Submit own forms",
      "Submit on behalf of org",
      "Manage org submissions",
      "View billing/invoices",
      "Make payments/purchase",
      "Manage payment methods",
      "View Memberships",
      "Manage/renew membership",
      "View Directory & pages",
    ],
  };

  return (
    <ModalOverlay>
      <ModalContent theme={theme}>
        {isLoading ? (
          <EditRoleSkeleton />
        ) : (
          <>
            <FormTitle
              title={id ? "Update Contact Role" : "Add New Contact Role"}
            />
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={setScopeSchema}
              onSubmit={handleSubmit}
            >
              {({ handleSubmit, setFieldValue, values, touched, errors }) => (
                <Form onSubmit={handleSubmit}>
                  <FormContainer theme={theme} className="p-3">
                    <StyledText
                      fontSize="14px"
                      fontWeight={500}
                      textColor={theme.CorporateTheme?.colors?.textSecondary}
                      className="fst-italic"
                      style={{ marginBottom: "40px" }}
                    >
                      {id
                        ? "Enter the contact role name, to update role"
                        : "Enter the contact role name, access level of the role and role scope to create a new role"}
                    </StyledText>

                    <div className="mb-3">
                      <StyledText
                        className=" mb-2"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                      >
                        Contact Role Name<span className="text-danger ">*</span>
                      </StyledText>
                      <FormikInputField
                        name="name"
                        placeholder="Enter contact role name here"
                        onChange={(e) => {
                          setFieldValue("name", e.target.value);
                          // dispatch(setQueryName({ name: e.target.value }));
                        }}
                      />
                    </div>
                    <div className="mt-3">
                      <StyledText
                        className=" mb-2"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                      >
                        Permission Type<span className="text-danger ">*</span>
                      </StyledText>{" "}
                      <FormikSelect
                        className={`${id && "class-disable not-allowed"}`}
                        options={[
                          {
                            label: "Viewer - Can only view data",
                            value: "Viewer",
                          },
                          {
                            label:
                              "Staff Contact - Can edit their own info and submit forms",
                            value: "Staff Contact",
                          },
                          {
                            label: "Manager - Can manage people in their group",
                            value: "Manager",
                          },
                          {
                            label: "Lead Contact - Full control including billing",
                            value: "Lead Contact",
                          },
                          {
                            label:
                              "Custom - Set permissions as per your requirements",
                            value: "Custom",
                          },
                        ]}
                        name="roleType"
                        placeholder="Select permission type"
                        onChange={(
                          val:
                            | string
                            | { label: string; value: number | string },
                        ) => {
                          const value =
                            typeof val === "string" ? val : val.value;
                          setFieldValue("roleType", value);
                          setSelectedRole(String(value));
                        }}
                        disabled={id ? true : false}
                      />
                      {selectedRole && selectedRole !== "Custom" && (
                        <ConDescription theme={theme}>
                            <AlertNotice 
                                message="Default role permissions are predefined and cannot be edited. To customize permissions, create a custom role."
                            />
                          <StyledText
                            fontSize="14px"
                            fontWeight={400}
                            className="mb-3"
                          >
                            <span className="fst-italic">
                              Permissions included with this role
                            </span>
                          </StyledText>
                          <ul className="ps-4">
                            {selectedRole == "Viewer"
                              ? role_type_detail?.Viewer?.map(
                                  (detail, index) => (
                                    <StyledText
                                      fontSize="14px"
                                      fontWeight={400}
                                      textColor={
                                        theme.CorporateTheme.colors.whiteBlack
                                      }
                                      className="mb-2"
                                      htmlTag={"li"}
                                      key={index}
                                    >
                                      <span className="fst-italic">
                                        {detail}
                                      </span>
                                    </StyledText>
                                  ),
                                )
                              : selectedRole == "Staff Contact"
                                ? role_type_detail?.staffContact?.map(
                                    (detail, index) => (
                                      <StyledText
                                        fontSize="14px"
                                        fontWeight={400}
                                        textColor={
                                          theme.CorporateTheme.colors.whiteBlack
                                        }
                                        className="mb-2"
                                        htmlTag={"li"}
                                        key={index}
                                      >
                                        <span className="fst-italic">
                                          {detail}
                                        </span>
                                      </StyledText>
                                    ),
                                  )
                                : selectedRole == "Manager"
                                  ? role_type_detail?.Manager?.map(
                                      (detail, index) => (
                                        <StyledText
                                          fontSize="14px"
                                          fontWeight={400}
                                          textColor={
                                            theme.CorporateTheme.colors
                                              .whiteBlack
                                          }
                                          className="mb-2"
                                          htmlTag={"li"}
                                          key={index}
                                        >
                                          <span className="fst-italic">
                                            {detail}
                                          </span>
                                        </StyledText>
                                      ),
                                    )
                                  : selectedRole == "Lead Contact"
                                    ? role_type_detail?.leadContact?.map(
                                        (detail, index) => (
                                          <StyledText
                                            fontSize="14px"
                                            fontWeight={400}
                                            textColor={
                                              theme.CorporateTheme.colors
                                                .whiteBlack
                                            }
                                            className="mb-2"
                                            htmlTag={"li"}
                                            key={index}
                                          >
                                            <span className="fst-italic">
                                              {detail}
                                            </span>
                                          </StyledText>
                                        ),
                                      )
                                    : null}
                          </ul>
                        </ConDescription>
                      )}
                      {/* <div className='mt-3'>
                                                    <FormikCheckBox
                                                        name='autoAssign'
                                                        label='Assign this role to the first contact to join an organization'
                                                        disabled={false}
                                                        borderColor={theme.CorporateTheme?.colors?.headerColor}
                                                        checkColor={theme.CorporateTheme?.colors?.headerColor}
                                                        color={theme.CorporateTheme.colors.textSecondary}
                                                    />
                                                </div> */}
                    </div>
                  </FormContainer>
                  <FormActions>
                    <Button
                      theme={theme}
                      type="button"
                      $variant="cancel"
                      onClick={onClose}
                    >
                      <CloseIcon
                        color={theme.CorporateTheme.colors.textSecondary}
                      />{" "}
                      Cancel
                    </Button>
                    <Button theme={theme} type="submit" $variant="add">
                      {isSubmitting ? (
                        <LoadingSpinner
                          size={"18px"}
                          color={theme.CorporateTheme.colors.background}
                        />
                      ) : (
                        <NewContactIcon
                          color={theme.CorporateTheme.colors.background}
                        />
                      )}
                      <label className="cursor-pointer">
                        {selectedRole === "Custom"
                          ? "Set Permission Roles"
                          : id
                            ? values?.roleType === "Custom"
                              ? "Update Permission Roles"
                              : "Update Contact Role"
                            : "Add Contact Role"}
                      </label>
                    </Button>
                  </FormActions>
                </Form>
              )}
            </Formik>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export const FormDescription = styled.span`
  font-size: 14px;
  font-family: "Raleway", sans-serif;
  font-weight: 500;
  font-style: italic;
  color: ${CorporateTheme.colors.textSecondary};
`;

export const ConDescription = styled("div")(({ theme }) => ({
  backgroundColor: theme?.CorporateTheme?.colors?.bannerBg,
  padding: "10px",
  borderRadius: "4px",
  marginTop: "10px",
}));

export default AddContactRoles;
